import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import futurathonBlack from '../assets/Logos/futurathon-black.png';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Почетна' },
    { path: '/about', label: 'За Хакатон' },
    { path: '/faq', label: 'FAQ' },
    { path: '/organizers', label: 'Организатори' },
    { path: '/competitors', label: 'Натпреварувачи' },
    { path: '/partners', label: 'Партнери' },
    { path: '/best', label: 'БЕСТ' },
    { path: '/itlabs', label: 'IT Labs' },
    { path: '/galerija', label: 'Галерија' },
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <header
      className="fixed top-0 left-0 right-0 transition-all duration-300"
      style={{
        zIndex: 10001,
        backgroundColor: isScrolled ? 'rgba(106, 49, 10, 0.95)' : 'rgba(106, 49, 10, 0.7)',
        backdropFilter: 'blur(10px)',
        borderBottom: isScrolled ? '2px solid #EFA74F' : '2px solid transparent',
        boxShadow: isScrolled ? '0 4px 30px rgba(0, 0, 0, 0.3)' : 'none',
      }}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" aria-label="Futurathon home">
            <div
              style={{
                backgroundColor: '#749269',
                borderRadius: 10,
                padding: '0 8px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                border: '2px solid #EFA74F',
              }}
            >
              <img
                src={futurathonBlack}
                alt="Futurathon"
                loading="eager"
                decoding="async"
                className="h-12 md:h-14 object-contain"
                style={{ display: 'block', imageRendering: 'auto', border: 'none', background: 'transparent', transform: 'scale(1.6)', transformOrigin: 'center', willChange: 'transform' }}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="px-4 py-2 rounded-lg text-base lg:text-lg font-medium transition-all duration-300 hover:scale-105"
                  style={{
                    color: isActive ? '#EFA74F' : '#DFC293',
                    backgroundColor: isActive ? 'rgba(239, 167, 79, 0.15)' : 'transparent',
                    borderBottom: isActive ? '2px solid #EFA74F' : '2px solid transparent',
                    fontFamily: 'Sagewold, system-ui, sans-serif',
                    textShadow: isActive ? '0 0 10px rgba(239, 167, 79, 0.5)' : 'none',
                  }}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* CTA Button (Desktop) */}
          <div className="hidden lg:block">
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSf6p-Cd071IJkln9JH2ZH535XMKNSCfqu1_2Koq6JIxkrnLhA/viewform"
              className="px-6 py-2.5 rounded-lg text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl"
              style={{
                backgroundColor: '#EFA74F',
                color: '#6A310A',
                boxShadow: '0 0 20px rgba(239, 167, 79, 0.4)',
                fontFamily: 'Zeitmax, system-ui, sans-serif',
              }}
            >
              Пријави се
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden w-10 h-10 flex flex-col items-center justify-center space-y-1.5 rounded-lg transition-all duration-300 hover:scale-110"
            style={{
              backgroundColor: isMenuOpen ? '#EFA74F' : 'rgba(239, 167, 79, 0.2)',
              border: '2px solid',
              borderColor: isMenuOpen ? '#6A310A' : '#749269',
            }}
            aria-label="Toggle menu"
          >
            <span
              className="block w-6 h-0.5 transition-all duration-300"
              style={{
                backgroundColor: isMenuOpen ? '#6A310A' : '#DFC293',
                transform: isMenuOpen ? 'rotate(45deg) translateY(8px)' : 'none',
              }}
            />
            <span
              className="block w-6 h-0.5 transition-all duration-300"
              style={{
                backgroundColor: isMenuOpen ? '#6A310A' : '#DFC293',
                opacity: isMenuOpen ? 0 : 1,
              }}
            />
            <span
              className="block w-6 h-0.5 transition-all duration-300"
              style={{
                backgroundColor: isMenuOpen ? '#6A310A' : '#DFC293',
                transform: isMenuOpen ? 'rotate(-45deg) translateY(-8px)' : 'none',
              }}
            />
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className="lg:hidden transition-all duration-300 ease-in-out"
          style={{
            maxHeight: isMenuOpen ? 'calc(100vh - 64px)' : '0',
            opacity: isMenuOpen ? 1 : 0,
            overflowY: isMenuOpen ? 'auto' : 'hidden',
            WebkitOverflowScrolling: isMenuOpen ? 'touch' : 'auto',
            paddingBottom: isMenuOpen ? '24px' : '0'
          }}
        >
          <div className="py-4 space-y-2">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block px-4 py-3 rounded-lg text-lg font-medium transition-all duration-300"
                  style={{
                    color: isActive ? '#EFA74F' : '#DFC293',
                    backgroundColor: isActive ? 'rgba(239, 167, 79, 0.2)' : 'rgba(116, 146, 105, 0.1)',
                    borderLeft: isActive ? '4px solid #EFA74F' : '4px solid transparent',
                    fontFamily: 'Sagewold, system-ui, sans-serif',
                    textShadow: isActive ? '0 0 10px rgba(239, 167, 79, 0.4)' : 'none',
                  }}
                >
                  {link.label}
                </Link>
              );
            })}
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSf6p-Cd071IJkln9JH2ZH535XMKNSCfqu1_2Koq6JIxkrnLhA/viewform"
              className="block px-4 py-3 rounded-lg text-lg font-semibold text-center transition-all duration-300 mt-4"
              style={{
                backgroundColor: '#EFA74F',
                color: '#6A310A',
                boxShadow: '0 4px 15px rgba(239, 167, 79, 0.4)',
                fontFamily: 'Zeitmax, system-ui, sans-serif',
              }}
            >
              Пријави се
            </a>
          </div>
        </div>
      </nav>

      {/* Futuristic glow line at bottom */}
      <div
        className="h-0.5 w-full transition-all duration-300"
        style={{
          background: isScrolled
            ? 'linear-gradient(90deg, transparent 0%, #EFA74F 50%, transparent 100%)'
            : 'transparent',
          boxShadow: isScrolled ? '0 0 10px rgba(239, 167, 79, 0.6)' : 'none',
        }}
      />
    </header>
  );
}

export default Header;
